<template>
  <div class="onboarding">
    <transition name="fade" mode="out-in">
      <component :is="currentPage.component" :nextOnboardingPage="nextPage" />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, shallowRef } from 'vue'
import { useRouter } from 'vue-router'

import { auth } from '@/services/auth'
import { storage } from '@/helpers/storage'
import { Mixpanel } from '@/analytics/mixpanel'
import { maybeSyncAnalyticsIds } from '@/analytics/logic'

import Questionnaire from '@/app/components/onboarding/pages/Questionnaire.vue'
import Signup from '@/app/components/onboarding/pages/Signup.vue'
import Trial from '@/app/components/onboarding/pages/Trial.vue'

const router = useRouter()

const pages = shallowRef([
  { name: 'signup', component: Signup },
  { name: 'questionnaire', component: Questionnaire },
  { name: 'trial', component: Trial },
])

const currentPageIndex = ref(0)
const currentPage = computed(() => {
  return pages.value[currentPageIndex.value]
})

onBeforeMount(async () => {
  if (auth.loggedIn() && !auth.isTemporaryUser()) {
    // Skip the onboarding if the user is logged in and
    // doesn't have a position saved (we clear the position
    // when the user has finished the questionnaire).
    if (!storage.getItem('sf_lq_position')) {
      router.replace({ name: 'home' })
      return
    }

    // Skip to the questionnaire if the user is logged in
    // and has a position saved in local storage.
    currentPageIndex.value = pages.value.findIndex(
      (page) => page.name === 'questionnaire',
    )
  }

  trackPageView()
})

async function nextPage(): Promise<void> {
  if (currentPageIndex.value + 1 < pages.value.length) {
    currentPageIndex.value++
    await maybeSyncAnalyticsIds()
    trackPageView()
    return
  }

  router.replace({ name: 'billing' })
}

function trackPageView(): void {
  // The questionnaire page has its own logic
  // for tracking page views (because it has
  // many sub pages).
  if (currentPage.value.name === 'questionnaire') {
    return
  }

  Mixpanel.trackOnboardingPageView(currentPage.value.name)
}
</script>

<style lang="scss" scoped>
.onboarding {
  --onboarding-padding: 24px;
  --onboarding-outer-width: 480px;
  --onboarding-inner-width: 360px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  background: $c-bg;
}

// Highlight tag that can be used inside
// the `title` and `subtitle` fields in the
// page configuration.
:deep(h1 span:not([class])),
:deep(h2 span:not([class])) {
  background: $c-yellow;
  padding-inline: 3px;
  padding-block: 4px 1px;
}

// Tag for adding a footnote to a
// `subtitle` paragraph.
:deep(span.source) {
  display: block;
  margin-top: 12px;
  font-size: 14px;
  letter-spacing: -0.02em;
  color: $c-black-6;
}
</style>
